import { useAuth0 } from '@auth0/auth0-react';
import { useQueries } from '@tanstack/react-query';

import { API_URL, apiFetch } from '@/config/api';
import {
  AGENT_TIERS,
  type AgentInfo,
  type AgentTier,
  type DaysWindow,
  type SampleMode,
  type TurnCurveResponse,
} from '@/types/cost-analytics';

async function fetchWithToken<T>(url: string, token: string): Promise<T> {
  const response = await apiFetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail?.user_error_message ?? 'Request failed');
  }
  return response.json() as Promise<T>;
}

export function useCostAgents() {
  const { getAccessTokenSilently } = useAuth0();

  return useQueries({
    queries: [
      {
        queryKey: ['cost-analytics', 'agents'],
        queryFn: async (): Promise<AgentInfo[]> => {
          const token = await getAccessTokenSilently();
          return fetchWithToken<AgentInfo[]>(
            `${API_URL}/admin/cost-analytics/agents`,
            token
          );
        },
      },
    ],
  })[0];
}

export interface TurnCurveQueryResult {
  data?: TurnCurveResponse;
  isLoading: boolean;
  isError: boolean;
}

export interface TierBaseline {
  tier: AgentTier;
  result: TurnCurveQueryResult;
}

export interface AgentSeries {
  agent: string;
  result: TurnCurveQueryResult;
}

export type TierFilter = AgentTier | 'all';

/**
 * Fires one baseline query per tier in scope (all 3 when ``tier === 'all'``,
 * just the selected tier otherwise), plus one query per selected agent. Tier
 * baselines drive the chart's dashed reference lines and the per-tier summary
 * table; agent series overlay on top when individual agents are picked.
 */
export function useAgentTurnCurves(
  agents: string[],
  sample: SampleMode,
  days: DaysWindow,
  tier: TierFilter
): { tierBaselines: TierBaseline[]; agentSeries: AgentSeries[] } {
  const { getAccessTokenSilently } = useAuth0();

  const tiersInScope: AgentTier[] = tier === 'all' ? [...AGENT_TIERS] : [tier];

  const tierQueries = tiersInScope.map((t) => ({
    queryKey: ['cost-analytics', 'turn-curve', { tier: t, sample, days }],
    queryFn: async (): Promise<TurnCurveResponse> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        sample,
        days: days.toString(),
        tier: t,
      });
      return fetchWithToken<TurnCurveResponse>(
        `${API_URL}/admin/cost-analytics/turn-curve?${params.toString()}`,
        token
      );
    },
  }));

  const agentQueries = agents.map((agent) => ({
    queryKey: ['cost-analytics', 'turn-curve', { agent, sample, days }],
    queryFn: async (): Promise<TurnCurveResponse> => {
      const token = await getAccessTokenSilently();
      const params = new URLSearchParams({
        sample,
        days: days.toString(),
        agent,
      });
      return fetchWithToken<TurnCurveResponse>(
        `${API_URL}/admin/cost-analytics/turn-curve?${params.toString()}`,
        token
      );
    },
  }));

  const results = useQueries({ queries: [...tierQueries, ...agentQueries] });

  const tierBaselines: TierBaseline[] = tiersInScope.map((t, idx) => ({
    tier: t,
    result: results[idx],
  }));
  const agentSeries: AgentSeries[] = agents.map((agent, idx) => ({
    agent,
    result: results[tiersInScope.length + idx],
  }));

  return { tierBaselines, agentSeries };
}
